import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Calendar,
  MapPin,
  Users,
  TrendingUp,
  Edit,
  Trash2,
  Eye,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, formatCurrency, getStatusColor } from '@utils';
import api from '@services/api';
import type { Event, ListResponse } from '@types';
import { ProgressBar } from '../../components/ui/ProgressBar';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
];

export default function EventsListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: events, isLoading } = useQuery({
    queryKey: ['events', { search: searchQuery, status: statusFilter, page: currentPage }],
    queryFn: async () => {
      return api.get<ListResponse<Event>>('/events', {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
      });
    },
  });

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted successfully');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleDuplicate = async (event: Event) => {
    try {
      await api.post(`/events/${event.id}/duplicate`);
      toast.success('Event duplicated successfully');
    } catch (error) {
      toast.error('Failed to duplicate event');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Events</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your events and track their performance
          </p>
        </div>
        <Link to="/events/create" className="btn-primary">
          <Plus className="w-4 h-4" />
          Create Event
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input"
            aria-label="Filter by Status"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="btn-secondary">
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Events Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-48 bg-gray-200 rounded-lg dark:bg-gray-700"></div>
              <div className="mt-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 dark:bg-gray-700"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events?.data?.map((event: Event, index: number) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="card card-hover overflow-hidden group"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={event.featuredImage || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop'}
                    alt={event.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={cn('badge', getStatusColor(event.status))}>
                      {event.status}
                    </span>
                  </div>
                  {event.isFeatured && (
                    <div className="absolute top-3 right-3">
                      <span className="badge bg-accent-100 text-accent-800 border-accent-200">
                        Featured
                      </span>
                    </div>
                  )}
                  {/* Actions */}
                  <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/events/${event.id}`}
                        className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/events/${event.id}/edit`}
                        className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                    {event.name}
                  </h3>

                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.startDate)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{event.venue?.name || 'Online Event'}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {event.soldCount}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Sold</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {event.capacity - event.soldCount}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(event.minTicketPrice || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">From</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-500 dark:text-gray-400">Sales Progress</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {event.capacity > 0 ? Math.round((event.soldCount / event.capacity) * 100) : 0}%
                      </span>
                    </div>
                    <div className="w-full">
                      <ProgressBar
                        percentage={event.capacity > 0 ? Math.round((event.soldCount / event.capacity) * 100) : 0}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {events?.meta && events.meta.lastPage > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(events.meta.currentPage - 1) * events.meta.perPage + 1} to{' '}
                {Math.min(events.meta.currentPage * events.meta.perPage, events.meta.total)} of{' '}
                {events.meta.total} events
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary p-2"
                  aria-label="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {events.meta.lastPage}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(events.meta.lastPage, p + 1))}
                  disabled={currentPage === events.meta.lastPage}
                  className="btn-secondary p-2"
                  aria-label="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
